import { useState } from 'react';
import '../../modules/SettingsDetailPage.css'; // full-page `.sd-*` detail frame
import './viewLock.css';
import { AppDataGrid, type AppColumn } from '../../../components/AppDataGrid';
import { ViewToggle } from '../../../components/ViewToggle';
import { ExportMenu } from '../../accounting/ExportMenu';
import { useNotify } from '../../../components/notifications';
import { useConfirm, useReasonGate, useSaveFlow } from '../../../components/confirm';
import { useHotkeys, formatCombo } from '../../../components/hotkeys';
import { guardedExit } from '../../../hooks/useUnsavedGuard';
import { runWithAuditReason } from '../../../api/client';
import {
  useGlossaryTranslations, useSaveGlossaryTranslation, useDeleteGlossaryTranslation,
  useSaveGlossaryDetail, GLOSSARY_LANGUAGES, errMessage,
  type GlossaryRow, type GlossaryTranslation,
} from './systemMasters';

interface Props {
  row: GlossaryRow;
  /** The list's rows, for the previous / next record arrows. */
  rows: GlossaryRow[];
  mode: 'view' | 'edit';
  setMode: (m: 'view' | 'edit') => void;
  /** Open another record (prev / next), keeping the current mode. */
  onOpen: (r: GlossaryRow) => void;
  onClose: () => void;
  onHome?: () => void;
}

const langName = (v: number) => GLOSSARY_LANGUAGES.find((l) => l.v === v)?.t ?? String(v);

/**
 * Settings ▸ System Management ▸ Glossary — the record's full-page Detail View (G5 Glossary_DetailView, owner
 * request 2026-10-15). Replaces the list while open, like the Country / Department details: title + breadcrumb +
 * record arrows, the action bar at the top and bottom (Save · Save and Close · Cancel — G5 has no Save and New,
 * Glossary has no New). Field ID / Field Name / Table Name / Module / BO are read-only; Acronym and Definition are
 * edited. Below, the Translations grid (dbo.Translations) — New · Edit · Delete · Export to, on the shared grid with
 * its Export / Maximize tools. A NEW translation is held until the record is saved (G5 commits the child collection
 * on the parent's Save); an edit or delete of a saved one applies at once.
 *
 * Opened from a row click it is read-only (Edit · Close) until Edit is pressed; from the row icon or the toolbar
 * Edit it opens ready to edit.
 */
export function GlossaryDetailView({ row, rows, mode, setMode, onOpen, onClose, onHome }: Props) {
  const notify = useNotify();
  const { confirm } = useConfirm();
  const gateReason = useReasonGate();
  const runSave = useSaveFlow();
  const save = useSaveGlossaryDetail();
  const viewOnly = mode === 'view';

  const [acronym, setAcronym] = useState(row.acronym ?? '');
  const [definition, setDefinition] = useState(row.definition ?? '');

  const { data: translations = [] } = useGlossaryTranslations(row.id);
  const saveTr = useSaveGlossaryTranslation(row.id);
  const delTr = useDeleteGlossaryTranslation(row.id);
  const [trSel, setTrSel] = useState<Set<string | number>>(new Set());
  const [trEdit, setTrEdit] = useState<{ id?: string; language: number; translation: string } | null>(null);
  const [trErr, setTrErr] = useState('');   // inline validation in the New / Edit Translation popup (§18.6)
  // New translations are STAGED until the record is saved; a staged row carries a "tmp-" id and cannot be edited yet.
  const [staged, setStaged] = useState<GlossaryTranslation[]>([]);
  const isStaged = (id: string | number) => String(id).startsWith('tmp-');
  const trRows = [...translations, ...staged];
  const selStaged = trSel.size === 1 && isStaged([...trSel][0]!);

  const leave = () => { void guardedExit(onClose); };
  const idx = rows.findIndex((r) => r.id === row.id);
  const prevRow = idx > 0 ? rows[idx - 1] : null;
  const nextRow = idx >= 0 && idx < rows.length - 1 ? rows[idx + 1] : null;

  /** Save through the shared flow: reason gate → "Do you want to Save?" → audited update (+ the held translations)
   *  → "Saved successfully." → stay on the record or return to the list. */
  const doSave = async (after: 'stay' | 'close') => {
    const reason = await gateReason('Glossary', 'edit');
    if (reason === null) return;
    await runSave({
      save: () => runWithAuditReason(reason, async () => {
        await save.mutateAsync({ id: row.id, acronym: acronym.trim() || null, definition: definition.trim() || null });
        if (staged.length) {
          for (const t of staged) await saveTr.mutateAsync({ language: t.language, translation: t.translation ?? '' });
          setStaged([]); setTrSel(new Set());
        }
      }),
      successMessage: 'Saved successfully.',
      onSaved: () => { if (after === 'close') onClose(); },
      onError: (e) => notify.error(errMessage(e)),
    });
  };

  const applyTr = async () => {
    if (!trEdit) return;
    const text = trEdit.translation.trim();
    if (!text) { setTrErr('Translation must not be empty.'); return; }
    // G5 rule: the same Language + Translation cannot repeat for this glossary entry (saved + held rows).
    if (trRows.some((t) => t.id !== trEdit.id && t.language === trEdit.language && (t.translation ?? '').trim() === text)) {
      setTrErr('This translation already exists.'); return;
    }
    setTrErr('');
    if (!trEdit.id) {
      const tmp: GlossaryTranslation = { id: `tmp-${crypto.randomUUID()}`, language: trEdit.language, translation: text };
      setStaged((s) => [...s, tmp]); setTrSel(new Set([tmp.id])); setTrEdit(null); return;
    }
    // Edit of a saved row → stored at once (the API re-checks uniqueness and audits it).
    try { await saveTr.mutateAsync({ id: trEdit.id, language: trEdit.language, translation: text }); setTrSel(new Set([trEdit.id])); setTrEdit(null); }
    catch (e) { setTrErr(errMessage(e)); }
  };
  const delSelectedTr = async () => {
    if (trSel.size === 0) { notify.warning('Select a translation to delete.'); return; }
    if (!await confirm({ title: 'Delete', tone: 'danger', confirmLabel: 'Delete', message: `Delete ${trSel.size} translation${trSel.size === 1 ? '' : 's'}?` })) return;
    const ids = [...trSel].map(String);
    try {
      const stagedIds = ids.filter(isStaged);
      if (stagedIds.length) setStaged((s) => s.filter((t) => !stagedIds.includes(t.id)));
      for (const id of ids.filter((i) => !isStaged(i))) await delTr.mutateAsync(id);
      setTrSel(new Set());
      notify.success('Deleted.');
    } catch (e) { notify.error(errMessage(e)); }
  };
  const openTrEdit = (v: { id?: string; language: number; translation: string }) => { setTrErr(''); setTrEdit(v); };
  const editSelectedTr = () => {
    const id = [...trSel][0]; if (!id || isStaged(id)) return;
    const t = trRows.find((x) => x.id === id); if (t) openTrEdit({ id: t.id, language: t.language, translation: t.translation ?? '' });
  };

  const trCols: AppColumn<GlossaryTranslation>[] = [
    // Row edit icon, as G5 (left of Language). A held (unsaved) row can be edited once the record is saved.
    { field: 'edit', header: '', headerText: 'Edit', noExport: true, sortable: false, filterable: false, width: 44, align: 'center', text: () => '',
      render: (t) => isStaged(t.id)
        ? <button className="pts-icon-btn edit" disabled title="Save the record to edit this translation"><i className="ti ti-edit" /></button>
        : <button className="pts-icon-btn edit" title="Edit" onClick={(e) => { e.stopPropagation(); openTrEdit({ id: t.id, language: t.language, translation: t.translation ?? '' }); }}><i className="ti ti-edit" /></button> },
    { field: 'language', header: 'Language', width: '40%', minWidth: 140, text: (r) => langName(r.language) },
    { field: 'translation', header: 'Translation', width: '60%', minWidth: 200, text: (r) => r.translation ?? '' },
  ];

  // Shortcuts through the shared hotkey layer (standing rule 14). The popup owns its own keys while open.
  useHotkeys(trEdit ? [] : viewOnly ? [
    { combo: 'alt+e', label: 'Edit', group: 'Glossary', order: 1, run: () => setMode('edit') },
    { combo: 'f2', label: 'Edit', group: 'Glossary', order: 2, run: () => setMode('edit') },
    { combo: 'esc', label: 'Close', group: 'Glossary', order: 3, allowInInput: false, run: onClose },
  ] : [
    { combo: 'mod+s', label: 'Save', group: 'Glossary', order: 1, run: () => void doSave('stay') },
    { combo: 'mod+enter', label: 'Save & Close', group: 'Glossary', order: 2, run: () => void doSave('close') },
    { combo: 'esc', label: 'Cancel', group: 'Glossary', order: 3, allowInInput: false, run: leave },
  ]);

  const recordName = (row.fieldName ?? '').trim() || row.fieldId || 'Glossary';
  const actions = (bottom?: boolean) => viewOnly ? (
    <div className={`sd-actionbar${bottom ? ' bottom' : ''}`}>
      <button className="sd-abtn primary" onClick={() => setMode('edit')} title={`Edit (${formatCombo('alt+e')})`}><span className="tb-i tb-i-blue">✎</span> Edit</button>
      <button className="sd-abtn cancel" onClick={onClose} title={`Close (${formatCombo('esc')})`}><span className="tb-i">↩︎</span> Close</button>
    </div>
  ) : (
    <div className={`sd-actionbar${bottom ? ' bottom' : ''}`}>
      <button className="sd-abtn primary" onClick={() => void doSave('stay')} disabled={save.isPending} title={`Save (${formatCombo('mod+s')})`}><span className="tb-i">💾</span> Save</button>
      <button className="sd-abtn" onClick={() => void doSave('close')} disabled={save.isPending} title={`Save and Close (${formatCombo('mod+enter')})`}><span className="tb-i">💾</span> Save and Close</button>
      <span className="sd-abtn-sep" aria-hidden="true" />
      <button className="sd-abtn cancel" onClick={leave} disabled={save.isPending} title={`Cancel (${formatCombo('esc')})`}><span className="tb-i">↩︎</span> Cancel</button>
    </div>
  );

  return (
    <div className="bl-page mlv-host">
      {/* List/Detail switch (as City / Payment Terms Setup): "List View" goes back to the list, asking first when
          there are unsaved changes. */}
      <div style={{ padding: '10px 16px 0', flex: '0 0 auto' }}>
        <ViewToggle view="detail" onChange={(v) => { if (v === 'list') leave(); }} />
      </div>
      <div className="bl-grid-area">
        <div className="sd-wrap">
          <div className="sd-head">
            <div>
              <h2>Glossary – {recordName}</h2>
              <div className="sd-bc">
                <a onClick={() => void guardedExit(() => onHome?.())}>Home</a><i className="ti ti-chevron-right" />
                <span>Settings</span><i className="ti ti-chevron-right" /><span>System Management</span><i className="ti ti-chevron-right" />
                <a onClick={leave}>Glossary</a><i className="ti ti-chevron-right" /><span>{recordName}</span>
              </div>
            </div>
            <div className="sd-navarrows">
              <button className="sd-na" title="Previous record" disabled={!prevRow || save.isPending} onClick={() => prevRow && void guardedExit(() => onOpen(prevRow))}>‹</button>
              <button className="sd-na next" title="Next record" disabled={!nextRow || save.isPending} onClick={() => nextRow && void guardedExit(() => onOpen(nextRow))}>›</button>
            </div>
          </div>

          {actions()}

          <div className="sd-body gloss-detail">
            <fieldset className="sm-viewlock gloss-fields" disabled={viewOnly}>
              <Field label="Field ID"><input className="acc-input gloss-ro" value={row.fieldId ?? ''} readOnly tabIndex={-1} /></Field>
              <Field label="Field Name" req><input className="acc-input gloss-ro" value={row.fieldName ?? ''} readOnly tabIndex={-1} /></Field>
              <Field label="Acronym"><input className="acc-input" autoFocus={!viewOnly} value={acronym} onChange={(e) => setAcronym(e.target.value)} /></Field>
              <Field label="Definition" tall><textarea className="acc-input gloss-def" rows={3} value={definition} onChange={(e) => setDefinition(e.target.value)} /></Field>
              <Field label="Table Name"><input className="acc-input gloss-ro" value={row.tableName ?? ''} readOnly tabIndex={-1} /></Field>
              {/* Module / BO are read-only dropdowns in G5 — a disabled select shows the value and its arrow. */}
              <Field label="Module" req><select className="acc-input gloss-ro" disabled><option>{row.module ?? ''}</option></select></Field>
              <Field label="BO" req><select className="acc-input gloss-ro" disabled><option>{row.bo ?? ''}</option></select></Field>
            </fieldset>

            <div className={`gloss-tr${viewOnly ? ' sm-viewlock-panel' : ''}`}>
              <div className="gloss-tr-title">Translations</div>
              <div className="acc-subgrid">
                <div className="acc-sub-toolbar">
                  <button className="ca-btn" onClick={() => openTrEdit({ language: 0, translation: '' })}><i className="ti ti-plus" /> New</button>
                  <button className="ca-btn" onClick={editSelectedTr} disabled={trSel.size !== 1 || selStaged}><i className="ti ti-edit" /> Edit</button>
                  <button className="ca-btn" onClick={() => void delSelectedTr()} disabled={trSel.size === 0}><i className="ti ti-trash" /> Delete</button>
                  <ExportMenu triggerClassName="ca-btn" filename="Glossary-Translations" headers={['Language', 'Translation']}
                    rows={trRows.map((t) => [langName(t.language), t.translation ?? ''])} />
                </div>
                {/* Detail-view grid standard (§18.6): the shared grid with its Export to + Maximize tools. */}
                <AppDataGrid<GlossaryTranslation> rows={trRows} columns={trCols} getRowId={(r) => r.id}
                  selectable selectedIds={trSel} onSelectionChange={setTrSel}
                  maximizable exportFilename="Glossary-Translations"
                  emptyText="No data to display" defaultPageSize={20} />
              </div>
            </div>
          </div>

          {actions(true)}
        </div>
      </div>

      {trEdit && (
        <div className="acc-overlay" onMouseDown={() => setTrEdit(null)}>
          <div className="acc-dialog gloss-tr-edit acc-dialog--accent" role="dialog" aria-modal="true" onMouseDown={(e) => e.stopPropagation()}>
            <div className="acc-dialog-hdr"><span>{trEdit.id ? 'Edit Translation' : 'New Translation'}</span><i className="ti ti-x acc-dialog-x" onClick={() => setTrEdit(null)} /></div>
            <div className="acc-dialog-body">
              {/* Language = radio group, single-line Translation — G5 parity. */}
              <Field label="Language" req tall>
                <div className="gloss-radio-group">
                  {GLOSSARY_LANGUAGES.map((l) => (
                    <label key={l.v} className="gloss-radio">
                      <input type="radio" name="gloss-tr-lang" checked={trEdit.language === l.v} onChange={() => { setTrErr(''); setTrEdit({ ...trEdit, language: l.v }); }} />
                      {l.t}
                    </label>
                  ))}
                </div>
              </Field>
              <Field label="Translation" req><input className="acc-input" autoFocus value={trEdit.translation} onChange={(e) => { setTrErr(''); setTrEdit({ ...trEdit, translation: e.target.value }); }}
                onKeyDown={(e) => { if (e.key === 'Enter') void applyTr(); if (e.key === 'Escape') setTrEdit(null); }} /></Field>
              {trErr && <div className="gloss-tr-err">{trErr}</div>}
            </div>
            <div className="acc-dialog-ftr">
              <button className="ca-btn ca-btn-solid" onClick={() => void applyTr()}><span className="tb-i">💾</span> OK</button>
              <button className="ca-btn" onClick={() => setTrEdit(null)}><span className="tb-i tb-i-grey">↩︎</span> Cancel</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

/** One caption + control row in the detail standard (caption beside its box, red star when required). */
function Field({ label, req, tall, children }: { label: string; req?: boolean; tall?: boolean; children: React.ReactNode }) {
  return (
    <div className={`acc-field-h${tall ? ' gloss-field-tall' : ''}`}>
      <label className={`acc-label${req ? ' acc-label-req' : ''}`}>{label}{req && <span className="req">*</span>}</label>
      <div className="acc-fh-control">{children}</div>
    </div>
  );
}
